import { useEffect, useState, useMemo } from "react";
import {
  Cpu, ShieldCheck, Sparkles, Palette, Zap, TrendingUp, Users, Activity,
  Leaf, DollarSign, Rocket, Layers, Info,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { useBranding } from "@/lib/branding";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const SECTION_ICONS = {
  security: ShieldCheck,
  collaboration: Users,
  productivity: TrendingUp,
  local_ai: Sparkles,
  creative: Palette,
};

const SECTION_COLORS = {
  security: "#DA291C",
  collaboration: "#0033A0",
  productivity: "#0F9B7F",
  local_ai: "#7C3AED",
  creative: "#F59E0B",
};

function Kpi({ label, value, sub, icon: Icon, accent, muted }) {
  return (
    <Card
      data-testid={`aipc-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className={`p-5 border-slate-200 shadow-sm ${accent ? "bg-[#0033A0] text-white border-transparent" : muted ? "bg-slate-50" : ""}`}
    >
      <div className={`flex items-center justify-between ${accent ? "text-white/70" : "text-slate-500"}`}>
        <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
        <Icon size={14} />
      </div>
      <div className={`font-display font-black text-3xl mt-1 ${accent ? "text-white" : "text-slate-900"}`}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${accent ? "text-white/70" : "text-slate-500"}`}>{sub}</div>}
    </Card>
  );
}

function ExecBar({ execution }) {
  const { cpu, npu, gpu } = execution;
  return (
    <div className="w-full">
      <div className="flex h-2 rounded-full overflow-hidden bg-slate-100">
        <div className="bg-slate-400" style={{ width: `${cpu}%` }} />
        <div className="bg-[#0033A0]" style={{ width: `${npu}%` }} />
        <div className="bg-[#DA291C]" style={{ width: `${gpu}%` }} />
      </div>
      <div className="flex justify-between text-[10px] mt-1 text-slate-500">
        <span>CPU {cpu}%</span>
        <span className="text-[#0033A0] font-semibold">NPU {npu}%</span>
        <span>GPU {gpu}%</span>
      </div>
    </div>
  );
}

function SectionCard({ section }) {
  const Icon = SECTION_ICONS[section.id] || Sparkles;
  const color = SECTION_COLORS[section.id] || "#0033A0";
  return (
    <Card
      data-testid={`aipc-section-${section.id}`}
      className="p-5 border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div
            className="w-10 h-10 rounded-md text-white grid place-items-center shrink-0"
            style={{ backgroundColor: color }}
          >
            <Icon size={18} />
          </div>
          <div>
            <div className="font-display font-bold text-slate-900 text-base leading-tight">{section.label}</div>
            <div className="text-[11px] text-slate-500">
              {section.app_count} app{section.app_count !== 1 ? "s" : ""} · {section.avg_tops_required}+ TOPS avg
            </div>
          </div>
        </div>
        {section.avg_execution.npu >= 60 && (
          <Badge className="bg-[#0033A0] hover:bg-[#0033A0] text-white text-[9px]">NPU dominant</Badge>
        )}
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Execution mix (avg)</div>
        <ExecBar execution={section.avg_execution} />
      </div>

      {section.latency_improvement_ms && (
        <div className="mt-3 border border-slate-200 rounded-md p-2.5 bg-slate-50">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Latency improvement</div>
          <div className="mt-1 text-xs">
            <span className="font-mono text-slate-500 line-through">{section.latency_improvement_ms.cpu_only} ms</span>
            <span className="mx-1.5 text-slate-400">→</span>
            <span className="font-mono text-[#0F9B7F] font-bold">{section.latency_improvement_ms.cpu_npu} ms</span>
            <span className="ml-2 text-[10px] text-[#0F9B7F] font-semibold">
              {Math.round((1 - section.latency_improvement_ms.cpu_npu / section.latency_improvement_ms.cpu_only) * 100)}% faster
            </span>
          </div>
        </div>
      )}

      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Sample apps</div>
        <ul className="mt-1.5 space-y-1">
          {section.apps.map((a) => (
            <li key={a.id} className="text-[11px] text-slate-700 flex items-center justify-between">
              <span className="truncate">{a.name} <span className="text-slate-400">· {a.vendor}</span></span>
              <span className="text-[9px] font-mono text-slate-400 shrink-0 ml-2">{a.npu_share}% NPU</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Assessment</div>
        <div className="text-[11px] font-semibold text-slate-900 text-right max-w-[65%]">{section.assessment}</div>
      </div>
    </Card>
  );
}

export default function AdminAIPC() {
  const { persona } = usePersona();
  const { brand } = useBranding();
  const [scope, setScope] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    endpoints
      .aipcDashboard(scope === "persona" ? persona.id : undefined)
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [scope, persona.id]);

  const npuWorkloadData = useMemo(() => {
    if (!data?.fleet?.npu_workload_share) return [];
    return Object.entries(data.fleet.npu_workload_share).map(([k, v]) => ({
      name: k.replace("_", " "),
      value: Math.round(v * 100),
    }));
  }, [data]);

  const personaAIData = useMemo(() => {
    if (!data?.fleet?.personas_ai_ready) return [];
    return Object.entries(data.fleet.personas_ai_ready).map(([k, v]) => ({
      persona: k.replace("_", " "),
      ready: Math.round(v * 100),
    }));
  }, [data]);

  if (loading || !data) {
    return (
      <div className="space-y-6" data-testid="admin-aipc-page-loading">
        <Skeleton className="h-12 w-96" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const { fleet, sections, top_apps, featured_models } = data;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-aipc-page">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#0033A0] grid place-items-center">
              <Cpu size={18} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{brand.small} · Flexspace · Admin</div>
              <div className="font-display font-bold text-slate-900 text-lg leading-tight">AI PC · Fleet Dashboard</div>
            </div>
          </div>
          <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">Admin view · Fleet-wide</Badge>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-8 py-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
            <Cpu size={12} className="text-[#0033A0]" /> AI PC POV · Where AI workloads actually execute
          </div>
          <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight mt-1">
            AI PC Fleet & Workload Dashboard
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Understand where {brand.orgName}&apos;s AI apps run — CPU vs NPU vs GPU — and how much of the fleet is ready for Copilot+ workloads. Data blends live app metadata with a Nexthink-derived fleet telemetry model.
          </p>
        </div>
        <Tabs value={scope} onValueChange={setScope} data-testid="aipc-scope-tabs">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="all" data-testid="scope-all" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">All fleet</TabsTrigger>
            <TabsTrigger value="persona" data-testid="scope-persona" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">{persona.name} only</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Fleet KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Fleet size" value={fleet.fleet_size.toLocaleString()} sub="Devices under management" icon={Cpu} accent />
        <Kpi label="AI PC share" value={`${Math.round(fleet.ai_pc_share * 100)}%`} sub="Copilot+ · 40+ TOPS" icon={Sparkles} />
        <Kpi label="Avg NPU utilization" value={`${Math.round(fleet.avg_npu_utilization * 100)}%`} sub="Across all Copilot+ PCs" icon={Activity} />
        <Kpi label="Sustainability" value={`${(fleet.monthly_inference_saved_kwh / 1000).toFixed(1)}k`} sub={`kWh saved / mo · $${fleet.monthly_cloud_cost_saved_usd.toLocaleString()} cloud-cost avoided`} icon={Leaf} muted />
      </div>

      {/* Five AI Workload Sections */}
      <div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
          <Zap size={12} className="text-[#0033A0]" /> Five AI workload categories
        </div>
        <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Where AI actually runs</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mt-4" data-testid="aipc-sections-grid">
          {sections.map((s) => <SectionCard key={s.id} section={s} />)}
        </div>
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* NPU workload share pie */}
        <Card className="p-6 border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            <Activity size={12} className="text-[#0033A0]" /> NPU workload distribution
          </div>
          <h2 className="font-display font-bold text-xl text-slate-900 mt-1">What&apos;s occupying the NPU?</h2>
          <div className="text-xs text-slate-500 mt-1">
            Aggregated across {fleet.fleet_size.toLocaleString()} {brand.orgName} endpoints (Nexthink telemetry).
          </div>
          <div className="h-72 mt-4" data-testid="npu-workload-chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={npuWorkloadData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%" cy="50%"
                  innerRadius={55} outerRadius={95}
                  paddingAngle={2}
                  label={(e) => `${e.name} ${e.value}%`}
                  labelLine={false}
                >
                  {npuWorkloadData.map((entry, i) => (
                    <Cell key={i} fill={SECTION_COLORS[entry.name.replace(" ", "_")] || "#94a3b8"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Personas AI readiness */}
        <Card className="p-6 border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            <Users size={12} className="text-[#0033A0]" /> AI-PC readiness by persona
          </div>
          <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Who has Copilot+ hardware?</h2>
          <div className="text-xs text-slate-500 mt-1">
            Share of each persona already running on a Copilot+ / 40+ TOPS device.
          </div>
          <div className="h-72 mt-4" data-testid="persona-ready-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={personaAIData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="persona" tick={{ fontSize: 10, fill: "#64748b", textTransform: "capitalize" }} />
                <YAxis tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                <Tooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} formatter={(v) => `${v}%`} />
                <Bar dataKey="ready" fill="#0033A0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Top apps + featured models */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            <Rocket size={12} className="text-[#0033A0]" /> Top installed AI apps
          </div>
          <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Fleet-wide adoption leaders</h2>
          <div className="mt-4 border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="top-apps-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">App</th>
                  <th className="text-left px-3 py-2 font-semibold">Category</th>
                  <th className="text-right px-3 py-2 font-semibold">NPU %</th>
                  <th className="text-right px-3 py-2 font-semibold">Installs</th>
                </tr>
              </thead>
              <tbody>
                {top_apps.map((a) => (
                  <tr key={a.id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-900">{a.name}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{a.category_label}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-900">{a.execution.npu}%</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-900">{a.install_count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            <Layers size={12} className="text-[#0033A0]" /> Featured NPU-ready models
          </div>
          <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Local models certified for Copilot+ PCs</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4" data-testid="featured-models">
            {featured_models.map((m) => (
              <div key={m.id} className="border border-slate-200 rounded-md p-3 hover:border-[#0033A0]/40 transition-colors">
                <div className="text-[10px] uppercase tracking-widest text-[#0033A0] font-semibold">{m.modality}</div>
                <div className="font-display font-bold text-slate-900 text-sm mt-0.5">{m.name}</div>
                <div className="text-[11px] text-slate-500">{m.vendor} · {m.params_b ? `${m.params_b}B` : "—"} params · {m.quantization}</div>
                <div className="mt-2 flex items-center justify-between text-[11px]">
                  <span className="text-slate-600"><span className="font-mono">{m.ttft_ms}</span> ms TTFT</span>
                  <span className="text-[#0F9B7F] font-mono font-semibold">{m.tokens_per_sec ? `${m.tokens_per_sec} tok/s` : ""}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* CTA banner */}
      <Card className="p-6 border-slate-200 shadow-sm bg-gradient-to-br from-[#0033A0] to-[#002573] text-white">
        <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-white/70 font-semibold">Executive takeaway</div>
            <h3 className="font-display font-bold text-2xl mt-1">AI PCs pay back where it matters — security & collaboration.</h3>
            <p className="text-sm text-white/80 mt-2 max-w-2xl">
              Continuous NPU inference (Falcon AI, Teams AI, Studio Effects) removes the CPU tax on always-on workloads.
              Local copilots (Phi Silica, Recall) unlock offline productivity. Creative workflows still lean on the GPU — plan the refresh accordingly.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center min-w-[240px]">
            <div className="bg-white/10 border border-white/20 rounded-md p-3">
              <Sparkles size={16} className="mx-auto mb-1" />
              <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">Copilot+ ready</div>
              <div className="font-display font-black text-lg">{Math.round(fleet.ai_pc_share * 100)}%</div>
            </div>
            <div className="bg-white/10 border border-white/20 rounded-md p-3">
              <DollarSign size={16} className="mx-auto mb-1" />
              <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">Cloud saved</div>
              <div className="font-display font-black text-lg">${(fleet.monthly_cloud_cost_saved_usd / 1000).toFixed(0)}k</div>
            </div>
            <div className="bg-white/10 border border-white/20 rounded-md p-3">
              <Leaf size={16} className="mx-auto mb-1" />
              <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">kWh saved</div>
              <div className="font-display font-black text-lg">{(fleet.monthly_inference_saved_kwh / 1000).toFixed(1)}k</div>
            </div>
          </div>
        </div>
      </Card>
      </div>
    </div>
  );
}
